import React, { useEffect, useState } from "react";
import { Buildings, X, Sparkle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Global onboarding wizard — appears the first time a user logs in and hasn't
 * set up their business profile. Dismissible ("I'll do this later") but nags
 * again on next login until they save. Never shows again once complete.
 */
export default function OnboardingModal() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    business_name: "",
    business_pitch: "",
    business_website: "",
    reply_to_email: "",
  });

  useEffect(() => {
    if (!user) return;
    // Skip for admin (Michael) — he doesn't need per-tenant setup
    if (user.role === "admin") return;
    // Skip if already onboarded
    if (user.onboarding_complete) return;
    // Skip if dismissed this session
    if (sessionStorage.getItem("cf_onboarding_dismissed") === "1") return;
    setForm((f) => ({ ...f, reply_to_email: user.email || "" }));
    // Small delay so the app frame settles first
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, [user]);

  const dismiss = () => {
    sessionStorage.setItem("cf_onboarding_dismissed", "1");
    setVisible(false);
  };

  const patch = (field, val) => setForm((f) => ({ ...f, [field]: val }));

  const save = async () => {
    if (!form.business_name.trim()) {
      toast.error("Business name is required");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/auth/business-profile", form);
      toast.success("You're all set! Every outreach email will now use your brand.");
      await refresh?.();
      setVisible(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="onboarding-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
    >
      <div className="bg-zinc-950 border border-orange-500/30 rounded-sm w-full max-w-2xl relative shadow-2xl my-8">
        <button
          onClick={dismiss}
          data-testid="onboarding-dismiss"
          className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-200"
          aria-label="Close"
        >
          <X size={16} weight="bold" />
        </button>

        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-1">
            <Sparkle size={16} weight="fill" className="text-orange-400" />
            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-orange-500">Welcome to ClickForge Lead Agent</span>
          </div>
          <h2 className="text-2xl font-display font-semibold text-zinc-100 mb-2">
            Let's set up your brand
          </h2>
          <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
            Your outreach emails send from YOUR business — not ours. Tell us who you are so every prospect sees your name, pitch, and signature. Takes 60 seconds.
          </p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="ob-name" className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
                Business name <span className="text-orange-400">*</span>
              </Label>
              <Input
                id="ob-name"
                value={form.business_name}
                onChange={(e) => patch("business_name", e.target.value)}
                placeholder="Acme Web Design"
                maxLength={120}
                data-testid="ob-name-input"
                autoFocus
                className="bg-zinc-900 border-zinc-700"
              />
            </div>

            <div>
              <Label htmlFor="ob-pitch" className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
                What you sell (1-2 sentences)
              </Label>
              <textarea
                id="ob-pitch"
                value={form.business_pitch}
                onChange={(e) => patch("business_pitch", e.target.value)}
                placeholder="We build fast Shopify stores for pet brands. Average 40% conversion lift in 30 days."
                maxLength={500}
                rows={2}
                data-testid="ob-pitch-input"
                className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-orange-500 focus:outline-none resize-y"
              />
              <div className="text-[10px] text-zinc-500 mt-1">
                💡 Example: "I help {`{industry}`} owners in {`{region}`} get more customers online through {`{service}`}."
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ob-web" className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
                  Website
                </Label>
                <Input
                  id="ob-web"
                  value={form.business_website}
                  onChange={(e) => patch("business_website", e.target.value)}
                  placeholder="https://yourbusiness.com"
                  maxLength={200}
                  data-testid="ob-web-input"
                  className="bg-zinc-900 border-zinc-700"
                />
              </div>
              <div>
                <Label htmlFor="ob-replyto" className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
                  Reply-to email
                </Label>
                <Input
                  id="ob-replyto"
                  type="email"
                  value={form.reply_to_email}
                  onChange={(e) => patch("reply_to_email", e.target.value)}
                  placeholder={user?.email || "you@yourdomain.com"}
                  data-testid="ob-replyto-input"
                  className="bg-zinc-900 border-zinc-700"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 p-3 bg-emerald-500/[0.06] border border-emerald-500/20 rounded-sm text-[12px] text-emerald-200 leading-relaxed">
            <strong>Good to know:</strong> Emails send from a shared verified address (<span className="font-mono">info@clickforgeds.com</span>) with YOUR business name as the display sender. Replies go straight to your reply-to inbox. You can upgrade to send from your own domain later.
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mt-6">
            <Button
              onClick={save}
              disabled={saving}
              data-testid="ob-save"
              className="flex-1 bg-orange-600 hover:bg-orange-500 text-zinc-950 font-medium"
            >
              {saving ? "Saving…" : "Save & start sending"}
            </Button>
            <Button
              type="button"
              onClick={() => {
                dismiss();
                navigate("/profile");
              }}
              variant="outline"
              data-testid="ob-later"
              className="flex-1 border-zinc-700"
            >
              I'll do this later
            </Button>
          </div>

          <div className="text-center text-[11px] font-mono text-zinc-600 mt-3">
            <Buildings size={11} weight="fill" className="inline mr-1" />
            You can edit these anytime from Profile → Business Profile
          </div>
        </div>
      </div>
    </div>
  );
}
